import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type MemberCard } from '../lib/api';
import { DISCIPLINES, dNum, plural, topDisciplines } from '../lib/domain';
import { filterMembers } from '../lib/lists';
import { useMe, useToast } from '../hooks';
import { Avatar, CountSelect, DiscChips, DiscIcon, ErrorBox, Icon, Loading, PageHead } from '../ui';

type View = 'active' | 'never' | 'former';
type BatchAction = 'invite' | 'former' | 'active';
const ACTION_TEXT: Record<BatchAction, { button: string; ask: (n: number) => string; done: string }> = {
  invite: {
    button: 'Einladen',
    ask: n =>
      `Einladung an ${plural(n, 'Mitglied', 'Mitglieder')} senden? Sie bekommen gleich eine E-Mail mit dem Link zum Sportplaner. Wer sich schon angemeldet hat oder in den letzten 24 Stunden eingeladen wurde, wird übersprungen.`,
    done: 'eingeladen',
  },
  former: {
    button: 'Ehemalig',
    ask: n =>
      `${plural(n, 'Mitglied', 'Mitglieder')} als ehemalig markieren? Sie werden überall abgemeldet, bekommen keine E-Mails mehr und sind für andere nicht mehr sichtbar. Termine und Teilnahmen bleiben erhalten.`,
    done: 'als ehemalig markiert',
  },
  active: {
    button: 'Wieder aktiv',
    ask: n => `${plural(n, 'Mitglied', 'Mitglieder')} wieder aktiv setzen?`,
    done: 'wieder aktiv',
  },
};

/** "37 eingeladen · übersprungen: schon angemeldet (2), keine E-Mail-Adresse (1)" */
function summary(r: { done: number; skipped: { reason: string }[] }, done: string) {
  const reasons = new Map<string, number>();
  for (const s of r.skipped) reasons.set(s.reason, (reasons.get(s.reason) ?? 0) + 1);
  const skipped = [...reasons].map(([why, n]) => `${why} (${n})`).join(', ');
  return `${r.done} ${done}${skipped ? ` · übersprungen: ${skipped}` : ''}`;
}

export default function Mitglieder() {
  const { data: me } = useMe();
  const admin = me?.role === 'admin';
  const qc = useQueryClient();
  const toast = useToast();
  // admins only: active members, members who never signed in, former members (the cards say who is admin)
  const [view, setView] = useState<View>('active');
  const status = view === 'former' ? 'former' : 'active';
  const [q, setQ] = useState('');
  const [discs, setDiscs] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<'active' | 'name'>('name'); // alphabetical by default
  // admins: select several members for one action
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [ask, setAsk] = useState<BatchAction | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ['members', 'directory', status],
    queryFn: () => api<MemberCard[]>(`/members/directory${status === 'former' ? '?status=former' : ''}`),
  });
  const neverOnly = admin && view === 'never';
  const list = useMemo(
    () => filterMembers(data ?? [], { q, discs, sort, neverOnly }),
    [data, q, discs, sort, neverOnly],
  );
  // only what is visible counts (a search after selecting hides members – they are not part of the action)
  const chosen = list.filter(m => selected.has(m.id)).map(m => m.id);
  const switchView = (v: View) => {
    setView(v);
    setSelected(new Set());
    setAsk(null);
  };
  const stopSelecting = () => {
    setSelecting(false);
    setSelected(new Set());
    setAsk(null);
  };
  const toggle = (id: number) =>
    setSelected(s => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const batch = useMutation({
    mutationFn: (action: BatchAction) =>
      api<{ done: number; skipped: { id: number; reason: string }[] }>('/members/batch', {
        method: 'POST',
        body: { action, ids: chosen },
      }),
    onSuccess: (r, action) => {
      toast(summary(r, ACTION_TEXT[action].done));
      stopSelecting();
      void qc.invalidateQueries({ queryKey: ['members'] });
    },
    onError: e => {
      setAsk(null);
      toast(e.message);
    },
  });
  const actions: BatchAction[] = view === 'former' ? ['active'] : ['invite', 'former'];
  const allChosen = list.length > 0 && chosen.length === list.length;

  return (
    <>
      <PageHead
        title="Mitglieder"
        right={
          admin && (
            <Link className="btn btn-ghost ph-add" to="/mitglieder/neu">
              <Icon name="plus" sw={2} />
              Mitglied hinzufügen
            </Link>
          )
        }
      />
      <div className="toolbar" role="search">
        <label className="field">
          <Icon name="search" sw={1.8} />
          <input
            type="search"
            placeholder="Name, Ort oder E-Mail suchen …"
            value={q}
            onChange={e => setQ(e.target.value)}
            aria-label="Mitglieder durchsuchen"
          />
        </label>
        {admin && (
          <div className="seg" role="group" aria-label="Status">
            <button aria-pressed={view === 'active'} onClick={() => switchView('active')}>
              Aktive
            </button>
            <button aria-pressed={view === 'never'} onClick={() => switchView('never')}>
              Nie angemeldet
            </button>
            <button aria-pressed={view === 'former'} onClick={() => switchView('former')}>
              Ehemalige
            </button>
          </div>
        )}
        <DiscChips value={discs} onChange={setDiscs} />
      </div>
      {status === 'former' && (
        <p className="note">
          Ehemalige Mitglieder sehen nur Admins. Sie können sich nicht anmelden und bekommen keine E-Mails.
        </p>
      )}
      {neverOnly && (
        <p className="note">
          Aktive Mitglieder, die sich noch nie angemeldet haben. „Auswählen“ → „Einladen“ schickt ihnen eine E-Mail mit
          dem Link zum Sportplaner und kurzen Hinweisen (App, Hilfe, Feedback).
        </p>
      )}
      {isLoading ? (
        <Loading />
      ) : error ? (
        <ErrorBox error={error} />
      ) : (
        <>
          <div className="resbar">
            <span>
              {selecting
                ? `${chosen.length} von ${plural(list.length, 'Person', 'Personen')} ausgewählt`
                : plural(list.length, 'Person', 'Personen')}
            </span>
            <CountSelect
              label="Sortierung"
              value={sort}
              onChange={setSort}
              options={[
                ['name', 'A–Z'],
                ['active', 'Aktivste'],
              ]}
            />
            {admin && (list.length > 0 || selecting) && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => (selecting ? stopSelecting() : setSelecting(true))}
              >
                {selecting ? 'Fertig' : 'Auswählen'}
              </button>
            )}
          </div>
          {selecting && (
            <div className="batchbar" role="group" aria-label="Sammelaktion">
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setSelected(allChosen ? new Set() : new Set(list.map(m => m.id)))}
              >
                {allChosen ? 'Keine' : `Alle auswählen (${list.length})`}
              </button>
              {actions.map(a => (
                <button
                  key={a}
                  className={a === 'former' ? 'btn btn-danger btn-sm' : 'btn btn-primary btn-sm'}
                  disabled={!chosen.length || batch.isPending}
                  onClick={() => setAsk(a)}
                >
                  {ACTION_TEXT[a].button} ({chosen.length})
                </button>
              ))}
              {ask && chosen.length > 0 && (
                <div className="confirm" role="alert">
                  <p>{ACTION_TEXT[ask].ask(chosen.length)}</p>
                  <button className="btn btn-ghost btn-sm" onClick={() => setAsk(null)}>
                    Abbrechen
                  </button>
                  <button
                    className={ask === 'former' ? 'btn btn-danger btn-sm' : 'btn btn-primary btn-sm'}
                    disabled={batch.isPending}
                    onClick={() => batch.mutate(ask)}
                  >
                    {batch.isPending ? 'Läuft …' : `${ACTION_TEXT[ask].button}: ${chosen.length}`}
                  </button>
                </div>
              )}
            </div>
          )}
          {list.length ? (
            <div className="mgrid">
              {list.map(m => {
                const body = (
                  <>
                    <Avatar person={{ id: m.id, name: `${m.first_name} ${m.last_name}`, photo: m.photo }} size="md" />
                    <span style={{ minWidth: 0 }}>
                      <b>
                        {m.first_name} {m.last_name}
                      </b>
                      <small>
                        {neverOnly
                          ? !m.email
                            ? 'keine E-Mail-Adresse'
                            : m.invited_at
                              ? `eingeladen am ${dNum(new Date(m.invited_at))}`
                              : 'noch nicht eingeladen'
                          : `${m.city ? `${m.city} · ` : ''}${plural(m.total, 'Teilnahme', 'Teilnahmen')}`}
                        {m.role === 'admin' ? ' · Admin' : ''}
                      </small>
                      <span className="mdisc">
                        {topDisciplines(m.disciplines).map(([k]) => (
                          <DiscIcon key={k} disc={k} size="xs" title={DISCIPLINES[k]} />
                        ))}
                      </span>
                    </span>
                  </>
                );
                return selecting ? (
                  <button
                    key={m.id}
                    type="button"
                    className="mcard mcard-sel"
                    aria-pressed={selected.has(m.id)}
                    onClick={() => toggle(m.id)}
                  >
                    {body}
                  </button>
                ) : (
                  <Link key={m.id} className="mcard" to={`/mitglieder/${m.id}`}>
                    {body}
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="empty">
              <b>Niemand gefunden.</b>
              <p>{neverOnly ? 'Alle haben sich schon angemeldet.' : 'Suche nach Vor- oder Nachnamen.'}</p>
            </div>
          )}
        </>
      )}
    </>
  );
}
